import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPromtDialog } from '../../common/UserPromtDialog';
import { useAppSelector } from '../../../hooks/redux-hooks';
import { Ellipsis, Pencil, Trash2 } from 'lucide-react';

interface TQuizItem {
  _id: string;
  name: string;
  skills: string[];
  learningOutcomes: string[];
  image?: string;
  description?: string;
  numQuestions: number;
  numAttempts: number;
  timeLimit: number;
  deadline?: string;
  status: string;
}

interface QuizItemProps {
  quiz: TQuizItem;
  deleteQuiz: (quizId: string) => void;
}

const QuizItem: FC<QuizItemProps> = ({ quiz, deleteQuiz }) => {
  const navigate = useNavigate();
  const [showUtils, setShowUtils] = useState(false);
  const [deletePrompt, setDeletePrompt] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { isSuperAdmin } = useAppSelector((state) => state.common);

  const isExpired = useMemo(() => {
    if (!quiz.deadline) return false;
    return new Date(quiz.deadline) < new Date();
  }, [quiz.deadline]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUtils(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  return (
    <div
      key={quiz._id}
      className="
        border-[#E2E8F0] border bg-white px-4 py-3 hover:border-[#020817]
        rounded-lg min-[200px] relative transition-all cursor-pointer
      "
      onClick={() => navigate(`/quiz/${quiz._id}`)}
    >
      <div className="flex justify-between items-center mb-4 relative text-[#020817]">
        <h4 className="text-[16px] font-semibold truncate">{quiz.name}</h4>
        <div
          className="w-[30px] h-[30px] rounded-full hover:bg-[#E2E8F0] flex justify-center items-center flex-shrink-0"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowUtils(!showUtils);
          }}
        >
          <Ellipsis color="#020817" size={16} />
        </div>
        {showUtils && (
          <div
            ref={menuRef}
            className="absolute right-[10px] top-[35px] w-[120px] shadow-md p-3 border border-[#E2E8F0] bg-white z-10"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <div
              className="flex items-center gap-2 pb-2 cursor-pointer hover:bg-[#F1F5F9] p-1 rounded"
              onClick={() => {
                navigate(`/quiz/${quiz._id}`);
              }}
            >
              <Pencil color="#020817" size={14} />
              <span className="text-[14px]">Edit</span>
            </div>
            {isSuperAdmin && (
              <div
                className="flex items-center gap-2 cursor-pointer hover:bg-[#FEE2E2] p-1 rounded"
                onClick={() => {
                  setDeletePrompt(true);
                }}
              >
                <Trash2 color="#DC2626" size={15} />
                <span className="text-[#DC2626] text-[14px]">Delete</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-1 flex-wrap mb-2">
        {quiz.skills.slice(0, 2).map((skill, idx) => (
          <span
            key={idx}
            className="text-[11px] bg-[#E2E8F0] text-[#020817] px-2 py-1 rounded"
          >
            {skill}
          </span>
        ))}
        {quiz.skills.length > 2 && (
          <span className="text-[11px] text-[#666]">
            +{quiz.skills.length - 2} more
          </span>
        )}
      </div>

      <div className="flex gap-3 text-[12px] text-[#999]">
        <span>{quiz.numQuestions} questions</span>
        <span>
          {quiz.numAttempts} attempt{quiz.numAttempts !== 1 ? 's' : ''}
        </span>
        {quiz.timeLimit > 0 && <span>{quiz.timeLimit} min</span>}
      </div>

      <div className="text-[12px] text-[#999] mt-1">
        {quiz.learningOutcomes?.length || 0} learning outcomes
      </div>
      <div className="flex items-center justify-between mt-3">
        {isExpired && (
          <span className="flex-shrink-0 text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded font-medium">
            Expired
          </span>
        )}
        <div
          className={`w-[10px] h-[10px] rounded-full ${quiz.status === 'pending' ? 'bg-yellow-500' : 'bg-green-500'}`}
        ></div>
      </div>

      {deletePrompt && (
        <UserPromtDialog
          title="Delete Quiz"
          message="Are you sure you want to delete this quiz? This action cannot be undone."
          submitButtonText="Delete"
          submitHandler={() => {
            deleteQuiz(quiz._id);
            setDeletePrompt(false);
          }}
          cancelHandler={() => setDeletePrompt(false)}
        />
      )}
    </div>
  );
};

export default QuizItem;
